import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Paper,
  Typography,
  Button,
  Box,
  Card,
  CardContent,
  Link,
  List,
  ListItem,
  ListItemText,
  Divider,
  CircularProgress,
  LinearProgress,
  Fade,
  Slide,
  Chip,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  CheckCircle,
  RadioButtonUnchecked,
  ArrowForward,
  ArrowBack,
} from '@mui/icons-material';
import { supabase } from '../../config/supabase';
import {
  NewHire,
  getNewHireGuestSession,
  getNewHireForGuestLogin,
  updateNewHireGuest,
} from '../../services/hireService';
import TrademarkFooter from '../common/TrademarkFooter';
import { StepContent } from '../../data/onboardingStepsData';
import { onboardingStepsService } from '../../services/onboardingStepsService';

const OnboardingSteps: React.FC = () => {
  const navigate = useNavigate();
  const [activeStep, setActiveStep] = useState(0);
  const [completedSteps, setCompletedSteps] = useState<number[]>([]);
  const [userHire, setUserHire] = useState<NewHire | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [updating, setUpdating] = useState(false);
  const [stepTransition, setStepTransition] = useState(true);
  const [steps, setSteps] = useState<StepContent[]>([]);
  /** True when user reached onboarding via passwordless new-hire login (no auth). */
  const [isGuest, setIsGuest] = useState(false);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isSmallMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Function to apply formatting to text
  const formatText = (text: string, formatting?: any, skipHyperlinks: boolean = false) => {
    if (!formatting) return text;

    let formattedText = text;
    
    // Apply dark bold formatting (theme-aware)
    if (formatting.darkBold) {
      formatting.darkBold.forEach((phrase: string) => {
        const regex = new RegExp(`(${phrase})`, 'gi');
        const textColor = theme.palette.mode === 'dark' ? '#ffffff' : '#1a1a1a';
        formattedText = formattedText.replace(regex, `<span style="font-weight: bold; color: ${textColor};">$1</span>`);
      });
    }

    // Apply bold formatting
    if (formatting.bold) {
      formatting.bold.forEach((phrase: string) => {
        const regex = new RegExp(`(${phrase})`, 'gi');
        formattedText = formattedText.replace(regex, '<strong>$1</strong>');
      });
    }

    // Apply underline formatting
    if (formatting.underline) {
      formatting.underline.forEach((phrase: string) => {
        const regex = new RegExp(`(${phrase})`, 'gi');
        formattedText = formattedText.replace(regex, '<u>$1</u>');
      });
    }

    // Apply underline + italic formatting
    if (formatting.underlineItalic) {
      formatting.underlineItalic.forEach((phrase: string) => {
        const regex = new RegExp(`(${phrase})`, 'gi');
        formattedText = formattedText.replace(regex, '<u><em>$1</em></u>');
      });
    }

    // Convert URLs and specific keywords to hyperlinks (only if not skipped)
    if (!skipHyperlinks) {
      formattedText = convertTextToHyperlinks(formattedText);
    }

    return formattedText;
  };

  // Function to format substeps
  const formatSubStep = (subStep: string, formatting?: any) => {
    if (!formatting) return subStep;

    let formattedSubStep = subStep;
    
    // Apply bold formatting to substeps
    if (formatting.bold) {
      formatting.bold.forEach((phrase: string) => {
        const regex = new RegExp(`"(${phrase})"`, 'gi');
        formattedSubStep = formattedSubStep.replace(regex, '"<strong>$1</strong>"');
      });
    }

    // Apply underline + italic formatting to substeps
    if (formatting.underlineItalic) {
      formatting.underlineItalic.forEach((phrase: string) => {
        const regex = new RegExp(`(${phrase})`, 'gi');
        formattedSubStep = formattedSubStep.replace(regex, '<u><em>$1</em></u>');
      });
    }

    // Convert URLs and specific keywords to hyperlinks
    formattedSubStep = convertTextToHyperlinks(formattedSubStep);

    return formattedSubStep;
  };

  // Function to convert text to hyperlinks
  const convertTextToHyperlinks = (text: string): string => {
    const linkColor = theme.palette.mode === 'dark' ? '#90caf9' : '#1976d2';
    const hoverColor = theme.palette.mode === 'dark' ? '#64b5f6' : '#1565c0';
    
    let convertedText = text;

    // 1. Convert full URLs (http/https)
    const urlRegex = /(https?:\/\/[^\s]+)/gi;
    convertedText = convertedText.replace(urlRegex, (match) => {
      return `<a href="${match}" target="_blank" rel="noopener noreferrer" style="color: ${linkColor}; text-decoration: none; transition: color 0.3s ease;" onmouseover="this.style.color='${hoverColor}'" onmouseout="this.style.color='${linkColor}'">${match}</a>`;
    });

    // 2. Convert specific keywords to their corresponding URLs
    const keywordMappings: { [key: string]: string } = {
      'getyourinsurancelicense': 'https://getyourinsurancelicense.com/marshall-weinberg?am_id=virtual1006',
      'nipr.com': 'https://nipr.com',
      'prepare2pass.com': 'https://prepare2pass.com',
      'xcelsolutions.com/gl30': 'https://partners.xcelsolutions.com/gl30',
      'xcelsolutions.com/ariasevanson': 'https://partners.xcelsolutions.com/ariasevanson',
      'zoom.us': 'https://zoom.us/download',
      'parallels.sjv.io': 'https://parallels.sjv.io/arias',
      'signup.live.com': 'https://signup.live.com/signup',
      'aws.impact.ailife.com': 'https://aws.impact.ailife.com/main',
      'sites.google.com': 'https://sites.google.com/view/ariaspartb/get-started',
      'thekeefersuccess.com': 'https://thekeefersuccess.com/eAppSetup.msi'
    };

    // Convert keywords to hyperlinks
    Object.entries(keywordMappings).forEach(([keyword, url]) => {
      const keywordRegex = new RegExp(`\\b${keyword}\\b`, 'gi');
      convertedText = convertedText.replace(keywordRegex, (match) => {
        return `<a href="${url}" target="_blank" rel="noopener noreferrer" style="color: ${linkColor}; text-decoration: none; transition: color 0.3s ease;" onmouseover="this.style.color='${hoverColor}'" onmouseout="this.style.color='${linkColor}'">${match}</a>`;
      });
    });

    return convertedText;
  };

  useEffect(() => {
    const fetchUserHire = async () => {
      try {
        setLoading(true);
        const { data: { user }, error: userError } = await supabase.auth.getUser();

        if (!userError && user) {
          // Authenticated flow: fetch new_hire by user.id
          const { data, error: hireError } = await supabase
            .from('new_hire')
            .select('*')
            .eq('id', user.id)
            .maybeSingle();

          if (hireError) throw hireError;
          if (!data) throw new Error('No hire data found for this user');

          setUserHire(data);
          setIsGuest(false);

          if (Number(data.progress) === 0) {
            const { data: updatedData, error: updateError } = await supabase
              .from('new_hire')
              .update({ progress: 1, update_timestamp: new Date().toISOString() })
              .eq('id', user.id)
              .select()
              .maybeSingle();
            if (!updateError && updatedData) setUserHire(updatedData);
          }

          const currentStep = Math.max(0, parseInt(data.checkListNum || '1', 10) - 1);
          setActiveStep(currentStep);
          setCompletedSteps(Array.from({ length: currentStep }, (_, i) => i));
          const orgId = Number(data.org_id) || 2;
          const loadedSteps = await onboardingStepsService.getOnboardingSteps(orgId);
          setSteps(loadedSteps);
          return;
        }

        // Guest flow: passwordless new-hire login (no auth)
        const guest = getNewHireGuestSession();
        if (!guest) {
          navigate('/login', { replace: true });
          return;
        }

        const data = await getNewHireForGuestLogin(guest.email);
        if (!data) {
          navigate('/login', { replace: true });
          return;
        }

        setUserHire(data);
        setIsGuest(true);

        if (Number(data.progress) === 0) {
          const updated = await updateNewHireGuest(data.id, {
            progress: 1,
            update_timestamp: new Date().toISOString(),
          });
          if (updated) setUserHire(updated);
        }

        const currentStep = Math.max(0, parseInt(data.checkListNum || '1', 10) - 1);
        setActiveStep(currentStep);
        setCompletedSteps(Array.from({ length: currentStep }, (_, i) => i));
        const orgId = Number(data.org_id) || 2;
        const loadedSteps = await onboardingStepsService.getOnboardingSteps(orgId);
        setSteps(loadedSteps);
      } catch (err: any) {
        console.error('Error fetching user hire data:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchUserHire();
  }, [navigate]);

  const handleNext = async () => {
    if (isStepComplete(activeStep)) {
      try {
        setUpdating(true);
        setStepTransition(false); // Start exit animation
        
        // Wait for exit animation
        setTimeout(async () => {
          if (!userHire) throw new Error('No user data available');

          // Log the current state
          console.log('Current user hire data:', userHire);
          console.log('Current active step:', activeStep);

          // Update the checkListNum to the next step number
          // Add 1 to convert from 0-based array index to 1-based step number
          const nextStepNum = (activeStep + 2).toString();
          console.log('Attempting to update checkListNum to:', nextStepNum);
          console.log('User ID being updated:', userHire.id);

          // Prepare the update data
          const updateData: Partial<NewHire> = { 
            checkListNum: nextStepNum,
            update_timestamp: new Date().toISOString()
          };
          
          // Update progress based on the next step number using automation rules
          // Get the organization ID from the user hire
          const orgId = userHire.org_id ? Number(userHire.org_id) : null;
          
          if (orgId) {
            // Try to get progress from automation rules
            const { checklistAutomationService } = await import('../../services/checklistAutomationService');
            const automatedProgress = await checklistAutomationService.getProgressForChecklistNum(orgId, nextStepNum);
            
            if (automatedProgress !== null) {
              updateData.progress = automatedProgress;
              console.log(`Updating progress to ${automatedProgress} for checklist ${nextStepNum} (from automation rules)`);
            } else {
              // Fallback: If no automation rule exists, use default mapping
              // This maintains backward compatibility
              if (nextStepNum === '2') {
                updateData.progress = 2;
                console.log('Updating progress to 2 for step 2 (fallback)');
              } else if (nextStepNum === '3') {
                updateData.progress = 3;
                console.log('Updating progress to 3 for step 3 (fallback)');
              } else if (nextStepNum === '4') {
                updateData.progress = 4;
                console.log('Updating progress to 4 for step 4 (fallback)');
              } else if (nextStepNum === '9') {
                updateData.progress = 5;
                console.log('Updating progress to 5 for step 9 (fallback)');
              } else if (nextStepNum === '14') {
                updateData.progress = 6;
                console.log('Updating progress to 6 for step 14 (fallback)');
              }
            }
          } else {
            console.warn('No org_id found, cannot use automation rules');
          }

          let data: NewHire | null = null;
          if (isGuest) {
            data = await updateNewHireGuest(userHire.id, {
              checkListNum: updateData.checkListNum,
              progress: updateData.progress,
              update_timestamp: updateData.update_timestamp,
            });
          } else {
            const result = await supabase
              .from('new_hire')
              .update(updateData)
              .eq('id', userHire.id)
              .select()
              .maybeSingle();
            if (result.error) throw result.error;
            data = result.data;
          }

          if (data) {
            setUserHire(data);
            setActiveStep((prevStep) => prevStep + 1);
          }
          
          // Start entrance animation for new step
          setTimeout(() => {
            setStepTransition(true);
            setUpdating(false);
          }, 100);
          
        }, 300);
        
      } catch (err: any) {
        console.error('Error updating checklist number:', err);
        console.error('Error details:', {
          message: err.message,
          code: err.code,
          details: err.details,
          hint: err.hint
        });
        setError(err.message);
        setStepTransition(true); // Reset on error
        setUpdating(false);
      }
    } else {
      console.log('Step not complete, cannot proceed to next step');
    }
  };

  const handleBack = () => {
    if (activeStep > 0) {
      setStepTransition(false);
      setTimeout(() => {
        setActiveStep((prevStep) => prevStep - 1);
        setTimeout(() => setStepTransition(true), 100);
      }, 300);
    }
  };

  const handleStepComplete = (stepIndex: number) => {
    if (stepIndex > activeStep) {
      return;
    }

    // Only allow marking as complete, not toggling back to incomplete
    if (!completedSteps.includes(stepIndex)) {
      setCompletedSteps([...completedSteps, stepIndex]);
    }
  };

  const isStepComplete = (stepIndex: number) => {
    return completedSteps.includes(stepIndex);
  };

  const canCompleteStep = (stepIndex: number) => {
    // Can only complete if it's the current step or earlier, and not already completed
    return stepIndex <= activeStep && !isStepComplete(stepIndex);
  };

  const isNextStepAvailable = () => {
    return isStepComplete(activeStep) && activeStep < steps.length - 1;
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Paper elevation={3} sx={{ p: 4 }}>
          <Typography color="error" align="center">
            {error}
          </Typography>
        </Paper>
      </Container>
    );
  }

  if (!userHire) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Paper elevation={3} sx={{ p: 4 }}>
          <Typography align="center">
            No onboarding data found for your account.
          </Typography>
        </Paper>
      </Container>
    );
  }

  const progressPercentage = steps.length > 0 ? ((activeStep + 1) / steps.length) * 100 : 0;

  return (
    <Container maxWidth="lg" sx={{ py: { xs: 2, md: 4 } }}>
      <Paper elevation={3} sx={{ 
        p: { xs: 2, md: 4 },
        background: theme.palette.mode === 'dark' 
          ? 'linear-gradient(135deg, rgba(255, 255, 255, 0.05) 0%, rgba(255, 255, 255, 0.1) 100%)'
          : 'linear-gradient(135deg, rgba(102, 126, 234, 0.1) 0%, rgba(118, 75, 162, 0.1) 100%)',
        backgroundColor: theme.palette.mode === 'dark' ? '#1e1e1e' : '#ffffff',
        boxShadow: theme.palette.mode === 'dark'
          ? '0 8px 32px rgba(0, 0, 0, 0.3)'
          : '0 8px 32px rgba(102, 126, 234, 0.15)',
        border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(102, 126, 234, 0.2)'}`,
        borderRadius: 3,
      }}>
        <Fade in={true} timeout={800}>
          <Box>
            <Typography variant="h4" component="h1" gutterBottom align="center" sx={{
              fontSize: { xs: '1.75rem', md: '2.125rem' },
              background: theme.palette.mode === 'dark'
                ? 'linear-gradient(135deg, #ffffff 0%, #b3b3b3 100%)'
                : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              fontWeight: 700,
            }}>
              {userHire?.fullName}'s Onboarding Process
            </Typography>
            <Typography variant="subtitle1" gutterBottom align="center" sx={{
              fontSize: { xs: '0.875rem', md: '1rem' },
              px: { xs: 1, md: 0 },
              color: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.8)' : theme.palette.text.secondary,
              fontWeight: 500,
            }}>
              Follow these steps exactly and concisely. Do not move to the next step until completing everything.
            </Typography>

            {/* Progress Bar and Step Counter */}
            <Box sx={{ my: { xs: 3, md: 4 } }}>
              <Box sx={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center', 
                mb: 2,
                flexDirection: { xs: 'column', sm: 'row' },
                gap: { xs: 1, sm: 0 }
              }}>
                <Chip 
                  label={`Step ${activeStep + 1} of ${steps.length}`} 
                  sx={{ 
                    fontSize: { xs: '0.75rem', sm: '0.875rem' },
                    background: theme.palette.mode === 'dark'
                      ? 'linear-gradient(135deg, rgba(255, 255, 255, 0.1) 0%, rgba(255, 255, 255, 0.15) 100%)'
                      : 'linear-gradient(135deg, rgba(102, 126, 234, 0.1) 0%, rgba(118, 75, 162, 0.1) 100%)',
                    color: theme.palette.mode === 'dark' ? '#ffffff' : theme.palette.text.primary,
                    fontWeight: 600,
                    border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(102, 126, 234, 0.2)'}`,
                  }}
                />
                <Typography variant="body2" sx={{
                  fontSize: { xs: '0.75rem', sm: '0.875rem' },
                  color: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.7)' : theme.palette.text.secondary,
                  fontWeight: 500,
                }}>
                  {Math.round(progressPercentage)}% Complete
                </Typography>
              </Box>
              <LinearProgress 
                variant="determinate" 
                value={progressPercentage} 
                sx={{ 
                  height: 8, 
                  borderRadius: 4,
                  backgroundColor: theme.palette.mode === 'dark' 
                    ? 'rgba(255,255,255,0.1)' 
                    : 'rgba(0, 0, 0, 0.1)',
                  '& .MuiLinearProgress-bar': {
                    borderRadius: 4,
                    background: 'linear-gradient(90deg, #ff6b6b, #4ecdc4, #45b7d1)',
                  }
                }} 
              />
            </Box>
          </Box>
        </Fade>

        {/* Animated Step Content */}
        <Fade in={stepTransition} timeout={600}>
          <Card sx={{ 
            mb: { xs: 3, md: 4 }, 
            minHeight: { xs: '300px', md: '400px' },
            background: theme.palette.mode === 'dark' 
              ? 'linear-gradient(135deg, rgba(255, 255, 255, 0.05) 0%, rgba(255, 255, 255, 0.1) 100%)'
              : 'linear-gradient(135deg, rgba(255, 255, 255, 0.8) 0%, rgba(255, 255, 255, 0.9) 100%)',
            border: theme.palette.mode === 'dark' 
              ? '1px solid rgba(255,255,255,0.15)' 
              : '1px solid rgba(255, 255, 255, 0.5)',
            backdropFilter: 'blur(10px)',
            borderRadius: 3,
            boxShadow: theme.palette.mode === 'dark'
              ? '0 8px 32px rgba(0, 0, 0, 0.2)'
              : '0 8px 32px rgba(102, 126, 234, 0.1)',
          }}>
            <CardContent sx={{ p: { xs: 2, md: 4 } }}>
              <Fade in={stepTransition} timeout={800} style={{ transitionDelay: '200ms' }}>
                <Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                    {isStepComplete(activeStep) ? (
                      <CheckCircle sx={{ 
                        color: '#4caf50', 
                        mr: 2, 
                        fontSize: { xs: 28, md: 32 },
                        flexShrink: 0,
                        filter: 'drop-shadow(0 2px 4px rgba(76, 175, 80, 0.3))',
                      }} />
                    ) : (
                      <RadioButtonUnchecked sx={{ 
                        color: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.5)' : 'rgba(0, 0, 0, 0.5)', 
                        mr: 2, 
                        fontSize: { xs: 28, md: 32 },
                        flexShrink: 0
                      }} />
                    )}
                    <Typography variant="h5" component="h2" sx={{ 
                      fontWeight: 600,
                      fontSize: { xs: '1.25rem', md: '1.5rem' },
                      color: theme.palette.mode === 'dark' ? '#ffffff' : theme.palette.text.primary
                    }}>
                      {steps[activeStep].title}
                    </Typography>
                  </Box>
                  
                  <List sx={{ 
                    '& .MuiListItem-root': {
                      transition: 'all 0.3s ease',
                      borderRadius: 2,
                      '&:hover': {
                        backgroundColor: theme.palette.mode === 'dark' 
                          ? 'rgba(255,255,255,0.05)' 
                          : 'rgba(102, 126, 234, 0.05)',
                        transform: 'translateX(4px)',
                      }
                    }
                  }}>
                    {steps[activeStep].content.map((item, index) => (
                      <React.Fragment key={index}>
                        <Fade in={stepTransition} timeout={600} style={{ transitionDelay: `${300 + (index * 100)}ms` }}>
                          <ListItem sx={{ py: 2, px: { xs: 1, md: 2 } }}>
                            <ListItemText
                              primary={
                                item.link ? (
                                  <Link 
                                    href={item.link} 
                                    target="_blank" 
                                    rel="noopener noreferrer"
                                    sx={{
                                      color: theme.palette.mode === 'dark' ? '#90caf9' : '#1976d2',
                                      '&:hover': {
                                        color: theme.palette.mode === 'dark' ? '#64b5f6' : '#1565c0'
                                      }
                                    }}
                                  >
                                    <span dangerouslySetInnerHTML={{ __html: formatText(item.text, item.formatting, true) }} />
                                  </Link>
                                ) : (
                                  <span 
                                    dangerouslySetInnerHTML={{ __html: formatText(item.text, item.formatting) }} 
                                    style={{ 
                                      color: theme.palette.text.primary 
                                    }}
                                  />
                                )
                              }
                              secondary={
                                item.subSteps && (
                                  <List dense>
                                    {item.subSteps.map((subStep, subIndex) => (
                                      <ListItem key={subIndex}>
                                        <ListItemText 
                                          primary={
                                            <span 
                                              dangerouslySetInnerHTML={{ __html: formatSubStep(subStep, item.formatting) }} 
                                              style={{ 
                                                color: theme.palette.text.secondary 
                                              }}
                                            />
                                          } 
                                        />
                                      </ListItem>
                                    ))}
                                  </List>
                                )
                              }
                            />
                          </ListItem>
                        </Fade>
                        {index < steps[activeStep].content.length - 1 && <Divider sx={{
                          borderColor: theme.palette.mode === 'dark' 
                            ? 'rgba(255,255,255,0.1)' 
                            : 'rgba(0,0,0,0.08)'
                        }} />}
                      </React.Fragment>
                    ))}
                  </List>
                </Box>
              </Fade>
            </CardContent>
          </Card>
        </Fade>

        {/* Navigation Buttons */}
        <Fade in={stepTransition} timeout={600} style={{ transitionDelay: '400ms' }}>
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            flexDirection: isMobile ? 'column' : 'row',
            gap: isMobile ? 2 : 1
          }}>
            {/* Mobile: Stack all buttons vertically */}
            {isMobile ? (
              <>
                <Button
                  variant="contained"
                  onClick={() => handleStepComplete(activeStep)}
                  color={isStepComplete(activeStep) ? 'success' : 'primary'}
                  disabled={!canCompleteStep(activeStep)}
                  fullWidth
                  sx={{ 
                    order: 1,
                    minHeight: 48,
                    fontSize: '0.875rem',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-2px)',
                    }
                  }}
                >
                  {isStepComplete(activeStep) ? 'Step Completed ✓' : 'Mark Step as Complete'}
                </Button>

                <Box sx={{ 
                  display: 'flex', 
                  width: '100%', 
                  gap: 2, 
                  order: 2 
                }}>
                  <Button
                    variant="outlined"
                    onClick={handleBack}
                    disabled={activeStep === 0 || updating}
                    startIcon={<ArrowBack />}
                    sx={{ 
                      flex: 1,
                      minHeight: 48,
                      fontSize: isSmallMobile ? '0.75rem' : '0.875rem',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        transform: 'translateX(-4px)',
                      }
                    }}
                  >
                    Back
                  </Button>

                  <Button
                    variant="contained"
                    onClick={handleNext}
                    disabled={!isNextStepAvailable() || updating}
                    color={activeStep === steps.length - 1 ? 'success' : 'primary'}
                    endIcon={updating ? <CircularProgress size={20} /> : <ArrowForward />}
                    sx={{ 
                      flex: 1,
                      minHeight: 48,
                      fontSize: isSmallMobile ? '0.75rem' : '0.875rem',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        transform: 'translateX(4px)',
                      }
                    }}
                  >
                    {updating ? 'Updating...' : activeStep === steps.length - 1 ? 'Done' : 'Next'}
                  </Button>
                </Box>
              </>
            ) : (
              /* Desktop: Horizontal layout */
              <>
                <Button
                  variant="outlined"
                  onClick={handleBack}
                  disabled={activeStep === 0 || updating}
                  startIcon={<ArrowBack />}
                  sx={{ 
                    minWidth: 120,
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateX(-4px)',
                    }
                  }}
                >
                  Back
                </Button>

                <Button
                  variant="contained"
                  onClick={() => handleStepComplete(activeStep)}
                  color={isStepComplete(activeStep) ? 'success' : 'primary'}
                  disabled={!canCompleteStep(activeStep)}
                  sx={{ 
                    minWidth: 180,
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-2px)',
                    }
                  }}
                >
                  {isStepComplete(activeStep) ? 'Step Completed ✓' : 'Mark Step as Complete'}
                </Button>

                <Button
                  variant="contained"
                  onClick={handleNext}
                  disabled={!isNextStepAvailable() || updating}
                  color={activeStep === steps.length - 1 ? 'success' : 'primary'}
                  endIcon={updating ? <CircularProgress size={20} /> : <ArrowForward />}
                  sx={{ 
                    minWidth: 120,
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateX(4px)',
                    }
                  }}
                >
                  {updating ? 'Updating...' : activeStep === steps.length - 1 ? 'Done' : 'Next'}
                </Button>
              </>
            )}
          </Box>
        </Fade>
      </Paper>
      <TrademarkFooter />
    </Container>
  );
};

export default OnboardingSteps; 